import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast, Toaster } from "sonner";
import {
  Plus,
  Trash,
  Archive,
  CheckCircle,
  FolderOpen,
  ArrowRight,
} from "@phosphor-icons/react";

const STATUS_ACTIONS = {
  draft: [
    { label: "Activate", next: "active", icon: CheckCircle, cls: "bg-emerald-700 hover:bg-emerald-800 text-white" },
  ],
  active: [
    { label: "Close", next: "closed", icon: Archive, cls: "border border-zinc-300 hover:bg-zinc-100" },
  ],
  closed: [
    { label: "Reopen", next: "active", icon: CheckCircle, cls: "border border-zinc-300 hover:bg-zinc-100" },
    { label: "Archive", next: "archived", icon: Archive, cls: "border border-zinc-300 hover:bg-zinc-100" },
  ],
  archived: [],
};

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [newName, setNewName] = useState("");
  const [newWh, setNewWh] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newType, setNewType] = useState("wall_to_wall");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/sessions");
    setSessions(data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!newName.trim()) {
      toast.error("Session name required");
      return;
    }

    setCreating(true);

    try {
      await api.post("/sessions", {
        session_name: newName.trim(),
        warehouse_name: newWh.trim(),
        description: newDesc.trim(),
        session_type: newType,
      });

      toast.success("Session created");

      setNewName("");
      setNewWh("");
      setNewDesc("");
      setNewType("wall_to_wall");

      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.post(`/sessions/${id}/status/${status}`);
      toast.success(`Session ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this draft session?")) return;

    try {
      await api.delete(`/sessions/${id}`);
      toast.success("Session deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/template/inventory.xlsx", {
        responseType: "blob",
      });

      const url = URL.createObjectURL(resp.data);

      const a = document.createElement("a");
      a.href = url;
      a.download = "inventory_template.xlsx";
      a.click();

      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell>
      <Toaster position="top-right" richColors />

      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-6">

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              Admin
            </div>

            <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">
              Inventory Sessions
            </h1>

            <p className="text-sm text-zinc-600">
              Create and manage Wall-to-Wall and Daily Movement sessions.
            </p>
          </div>

          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-100 px-3 py-2 text-sm"
          >
            <FolderOpen className="w-4 h-4" />
            Download Excel Template
          </button>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 font-semibold">
            New Session
          </div>

          <div className="p-4 grid md:grid-cols-5 gap-3">

            <input
              className="rounded-sm border border-zinc-300 px-3 py-2 text-sm"
              placeholder="Session name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />

            <input
              className="rounded-sm border border-zinc-300 px-3 py-2 text-sm"
              placeholder="Warehouse"
              value={newWh}
              onChange={(e) => setNewWh(e.target.value)}
            />

            <input
              className="rounded-sm border border-zinc-300 px-3 py-2 text-sm"
              placeholder="Description"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />

            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="rounded-sm border border-zinc-300 px-3 py-2 text-sm"
            >
              <option value="wall_to_wall">
                Wall To Wall Inventory
              </option>

              <option value="daily_movement">
                Daily Movement
              </option>
            </select>

            <button
              disabled={creating}
              onClick={create}
              className="rounded-sm bg-indigo-950 hover:bg-indigo-900 text-white px-4 py-2 text-sm"
            >
              {creating ? "Creating..." : "Create Session"}
            </button>

          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 font-semibold">
            All Sessions ({sessions.length})
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">

              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Warehouse</th>
                  <th>Status</th>
                  <th>SKUs</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>

              <tbody>

                {sessions.map((s) => (
                  <tr key={s.id}>

                    <td>
                      <Link
                        to={`/admin/sessions/${s.id}`}
                        className="font-medium text-indigo-900 hover:underline"
                      >
                        {s.session_name}
                      </Link>

                      <div className="text-xs text-zinc-500">
                        {s.description || ""}
                      </div>
                    </td>

                    <td>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        s.session_type === "daily_movement"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-blue-100 text-blue-800"
                      }`}>
                        {s.session_type === "daily_movement"
                          ? "Daily Movement"
                          : "Wall To Wall"}
                      </span>
                    </td>

                    <td>{s.warehouse_name || "—"}</td>

                    <td>{s.session_status}</td>

                    <td>{s.item_count}</td>

                    <td className="text-xs">
                      {new Date(s.created_at).toLocaleString()}
                    </td>

                    <td className="text-right space-x-1">

                      {(STATUS_ACTIONS[s.session_status] || []).map((a) => (
                        <button
                          key={a.label}
                          onClick={() => changeStatus(s.id, a.next)}
                          className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs ${a.cls}`}
                        >
                          <a.icon className="w-3 h-3" />
                          {a.label}
                        </button>
                      ))}

                      <Link
                        to={`/admin/sessions/${s.id}`}
                        className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-zinc-300 hover:bg-zinc-100"
                      >
                        Open
                        <ArrowRight className="w-3 h-3" />
                      </Link>

                      {s.session_status === "draft" && (
                        <button
                          onClick={() => del(s.id)}
                          className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50"
                        >
                          <Trash className="w-3 h-3" />
                          Delete
                        </button>
                      )}

                    </td>
                  </tr>
                ))}

              </tbody>

            </table>
          </div>
        </div>

      </div>
    </AppShell>
  );
}
